if (!customElements.get('ac-media-reviews')) {
  customElements.define('ac-media-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('.ac-press__track');
      this.cards = [...this.track.querySelectorAll('.ac-press__card')];
      this.button = this.querySelector('.ac-press__motion');
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const options = { signal: this.controller.signal };
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.pointer = null;
      this.visible = false;
      this.position = this.track.scrollLeft;
      this.cooldown = 0;
      this.suppressClickUntil = 0;
      this.cards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.dataset.mediaClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        clone.removeAttribute('data-shopify-editor-block');
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        this.track.append(clone);
      });
      this.clones = [...this.track.querySelectorAll('[data-media-clone]')];
      this.measure = () => {
        this.cycle = this.clones[0] && !this.motion.matches ? this.clones[0].offsetLeft - this.cards[0].offsetLeft : 0;
        this.position = this.track.scrollLeft;
      };
      this.applyMotionPreference = () => {
        this.stop();
        this.clones.forEach(card => { card.hidden = this.motion.matches; });
        this.button.hidden = this.motion.matches || this.cards.length < 2;
        this.measure();
        this.start();
      };
      this.motion.addEventListener('change', this.applyMotionPreference, options);
      this.track.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.stop();
      }, options);
      this.track.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.start();
      }, options);
      this.track.addEventListener('focusin', () => { this.focused = true; this.stop(); }, options);
      this.track.addEventListener('focusout', event => {
        if (this.track.contains(event.relatedTarget)) return;
        this.focused = false;
        this.position = this.track.scrollLeft;
        this.start();
      }, options);
      this.track.addEventListener('pointerdown', event => {
        if (event.button !== 0) return;
        this.pointer = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft, mouse: event.pointerType === 'mouse', dragged: false };
        this.stop();
      }, options);
      this.track.addEventListener('pointermove', event => {
        const state = this.pointer;
        if (!state || event.pointerId !== state.id || !state.mouse) return;
        const delta = event.clientX - state.x;
        if (!state.dragged && Math.abs(delta) < 6) return;
        if (!state.dragged) {
          state.dragged = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        event.preventDefault();
        this.track.scrollLeft = state.left - delta;
      }, options);
      const endPointer = event => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        if (this.pointer.dragged) this.suppressClickUntil = performance.now() + 500;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.pointer = null;
        this.track.classList.remove('is-dragging');
        this.deferResume();
      };
      window.addEventListener('pointerup', endPointer, options);
      window.addEventListener('pointercancel', endPointer, options);
      this.track.addEventListener('click', event => {
        if (performance.now() < this.suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
      }, { ...options, capture: true });
      this.track.addEventListener('dragstart', event => event.preventDefault(), options);
      this.track.addEventListener('wheel', () => this.deferResume(), { ...options, passive: true });
      this.track.addEventListener('scroll', () => {
        if (!this.frame) this.position = this.track.scrollLeft;
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const step = this.cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).gap) || 0);
        const left = event.key === 'Home' ? 0 : event.key === 'End' ? step * (this.cards.length - 1) : this.track.scrollLeft + (event.key === 'ArrowRight' ? step : -step);
        this.track.scrollTo({ left, behavior: 'instant' });
        this.position = this.track.scrollLeft;
      }, options);
      this.button.addEventListener('click', () => {
        this.paused = !this.paused;
        this.button.setAttribute('aria-pressed', String(this.paused));
        this.button.querySelector('[data-motion-label]').textContent = this.paused ? 'Resume scrolling' : 'Pause scrolling';
        this.button.querySelector('[data-motion-icon]').textContent = this.paused ? '▷' : 'Ⅱ';
        this.paused ? this.stop() : this.start();
      }, options);
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start(), options);
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.ac-press__card');
        if (!card) return;
        this.paused = true;
        this.stop();
        this.track.scrollLeft = card.offsetLeft - this.cards[0].offsetLeft;
        this.position = this.track.scrollLeft;
        this.button.setAttribute('aria-pressed', 'true');
        this.button.querySelector('[data-motion-label]').textContent = 'Resume scrolling';
      }, options);
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.track);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.visible ? this.start() : this.stop();
      });
      this.intersectionObserver.observe(this);
      this.applyMotionPreference();
    }
    deferResume() {
      this.stop();
      this.cooldown = performance.now() + 4000;
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => { this.position = this.track.scrollLeft; this.start(); }, 4050);
    }
    start() {
      if (this.frame || !this.visible || document.hidden || this.motion.matches || this.paused || this.hovered || this.focused || this.pointer || performance.now() < this.cooldown || !this.cycle) return;
      this.position = this.track.scrollLeft;
      this.lastTime = null;
      this.frame = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      const delta = this.lastTime === null ? 0 : Math.min(time - this.lastTime, 64);
      this.lastTime = time;
      this.position += delta * 0.045;
      if (this.position >= this.cycle) this.position %= this.cycle;
      this.track.scrollLeft = this.position;
      this.frame = requestAnimationFrame(next => this.tick(next));
    }
    stop() { cancelAnimationFrame(this.frame); this.frame = 0; }
    disconnectedCallback() {
      this.stop();
      clearTimeout(this.resumeTimer);
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.clones?.forEach(card => card.remove());
    }
  });
}
